import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Sparkles, Upload, Camera } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { useConsultation } from "@/components/ConsultationProvider";
import { formatPhone } from "@/utils/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FINISHES = [
  { id: "metallic", label: "Metallic Finish", colors: ["Gold Lava", "Obsidian Pearl", "Silver Smoke", "Ocean Blue"] },
  { id: "flake", label: "Decorative Flake", colors: ["Domino Black & White", "Tidal Tan", "Nightfall Gray", "Shoreline Sand"] },
  { id: "polished", label: "Polished Concrete", colors: ["Natural Gray", "Warm Cream", "Charcoal"] },
];

export default function FloorVisualizer() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [finish, setFinish] = useState("metallic");
  const [color, setColor] = useState(FINISHES[0].colors[0]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const { open } = useConsultation();

  const activeFinish = FINISHES.find((f) => f.id === finish);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const changeFinish = (e) => {
    const next = FINISHES.find((f) => f.id === e.target.value);
    setFinish(next.id);
    setColor(next.colors[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Add a photo of your floor first.");
      return;
    }
    setLoading(true);
    try {
      await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          phone,
          zip: zip.trim(),
          service: `AI Preview — ${activeFinish.label} (${color})`,
          message: "Requested an AI floor preview on the website.",
        }),
      }).catch(() => {});

      const fd = new FormData();
      fd.append("file", file);
      fd.append("finish", finish);
      fd.append("color", color);
      const res = await fetch(`${API}/visualize`, { method: "POST", body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(typeof data?.detail === "string" ? data.detail : "The visualizer is busy — please try again in a moment.");
      }
      const data = await res.json();
      setResult(`${BACKEND}${data.url}`);
      toast.success("Your preview is ready — and we've got your number if you'd like the real thing.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  return (
    <div className="gold-ring-card rounded-xl p-7 sm:p-9" data-testid="floor-visualizer">
      {!result ? (
        <form onSubmit={submit} className="space-y-5" data-testid="visualizer-form">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickFile} data-testid="visualizer-camera-input" />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="visualizer-file-input" />
          {preview ? (
            <div className="border-2 border-dashed border-amber-600/30 rounded-lg p-4 flex flex-col items-center gap-2">
              <img src={preview} alt="Your floor" className="max-h-48 rounded-md object-cover" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                data-testid="visualizer-camera-btn"
                className="border-2 border-dashed border-amber-600/30 hover:border-amber-600/60 rounded-lg p-5 flex flex-col items-center gap-2 text-stone-500 hover:text-amber-800 transition-colors duration-300"
              >
                <Camera className="w-7 h-7 text-amber-700" />
                <span className="text-xs font-semibold text-center leading-snug">Take a photo of your floor</span>
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="visualizer-upload-btn"
                className="border-2 border-dashed border-amber-600/30 hover:border-amber-600/60 rounded-lg p-5 flex flex-col items-center gap-2 text-stone-500 hover:text-amber-800 transition-colors duration-300"
              >
                <Upload className="w-7 h-7 text-amber-700" />
                <span className="text-xs font-semibold text-center leading-snug">Upload from your photos</span>
              </button>
            </div>
          )}
          {preview && (
            <button type="button" onClick={reset} data-testid="visualizer-change-photo-btn" className="text-xs text-amber-700 hover:text-amber-800 flex items-center gap-1">
              <RotateCcw className="w-3.5 h-3.5" /> Choose a different photo
            </button>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Finish</span>
              <select className="input-dark mt-1.5" value={finish} onChange={changeFinish} data-testid="visualizer-finish-select">
                {FINISHES.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Color</span>
              <select className="input-dark mt-1.5" value={color} onChange={(e) => setColor(e.target.value)} data-testid="visualizer-color-select">
                {activeFinish.colors.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="border-t border-amber-600/15 pt-5 space-y-4">
            <p className="text-xs text-stone-500 leading-relaxed">
              Enter your name, phone number, and zip code below to generate your floor preview. By clicking Show Me My Floor, you're also giving Crownvera permission to contact you about your flooring project.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required className="input-dark" placeholder="First name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="visualizer-first-name-input" />
              <input required className="input-dark" placeholder="Last name *" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="visualizer-last-name-input" />
              <input required className="input-dark" placeholder="Phone number *" type="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} data-testid="visualizer-phone-input" />
              <input required className="input-dark" placeholder="Zip code *" inputMode="numeric" maxLength={5} value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))} data-testid="visualizer-zip-input" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="visualizer-submit-btn"
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide uppercase text-sm py-4 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Pouring your virtual floor..." : "Show Me My Floor"}
          </button>
          {loading && <p className="text-xs text-stone-500 text-center">AI is rendering your floor — this takes about 20–30 seconds.</p>}
        </form>
      ) : (
        <div data-testid="visualizer-result">
          <BeforeAfterSlider beforeUrl={preview} afterUrl={result} title="Your floor, reimagined" />
          <p className="mt-4 text-xs text-stone-500 text-center">
            AI concept preview — your real Crownvera floor will be one-of-one and even richer in person.
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <button
              onClick={open}
              data-testid="visualizer-cta-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm py-3.5 rounded-md shadow-md transition-colors duration-300"
            >
              Make It Real — Free Consultation
            </button>
            <button
              onClick={reset}
              data-testid="visualizer-reset-btn"
              className="border border-amber-600/40 hover:border-amber-600 text-amber-800 font-semibold uppercase tracking-wider text-sm py-3.5 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Try Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
